import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Moon, Download, Loader2, Sparkles } from 'lucide-react';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';

const TEAL     = '#4AADA8';
const LAVENDER = '#A99CC8';

/* ── Format: "June 8, 2026" ── */
const formatCertDate = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${d.getDate()}, ${d.getFullYear()}`;
};

/* ════════════════════════ MAIN PAGE ════════════════════════ */
const ShahadahCertificatePage = ({ onBack }) => {
  const { profile } = useAuth();
  const [step, setStep]               = useState(1);
  const [name, setName]               = useState(profile?.display_name || '');
  const [date, setDate]               = useState('');
  const [confirmed, setConfirmed]     = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [announce, setAnnounce]       = useState('');
  const certRef = useRef(null);

  const canGenerate = name.trim().length > 0 && date && confirmed;

  const handleGenerate = async () => {
    if (!canGenerate || generating) return;
    setGenerating(true);
    try {
      if (profile?.id) await api.claimShahadahBadge(profile.id);
    } catch { /* badge award is best-effort — certificate still shown */ }
    setGenerating(false);
    setStep(2);
    setAnnounce('Your certificate has been generated.');
  };

  const handleDownload = async () => {
    if (!certRef.current || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        allowTaint: false,
        imageTimeout: 10000,
      });
      const link = document.createElement('a');
      const safeName = (name.trim() || 'shahadah')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      link.download = `${safeName}-shahadah-certificate.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setAnnounce('Certificate downloaded.');
    } catch {
      setAnnounce('Could not generate the download. Please try again.');
    }
    setDownloading(false);
  };

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-24" data-testid="shahadah-certificate-page">
      <div aria-live="polite" className="sr-only">{announce}</div>

      {/* ── Page header ── */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={onBack}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
          aria-label="Go back"
          data-testid="shahadah-back-btn"
        >
          <ChevronLeft className="w-6 h-6 text-primary" aria-hidden="true" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
            <Moon className="w-6 h-6" style={{ color: TEAL }} aria-hidden="true" />
            Shahadah Certificate
          </h1>
          <p className="text-sm text-text-muted mt-0.5">
            Commemorate your declaration of faith with a personalised keepsake
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">

        {/* ══════════════ STEP 1 — Claim form ══════════════ */}
        {step === 1 && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
            className="rounded-arch-xl p-6 lg:p-8"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="flex items-start gap-3 mb-6 p-4 rounded-arch text-sm leading-relaxed"
              style={{ background: 'rgba(74,173,168,0.08)', border: '1px solid rgba(74,173,168,0.18)' }}
            >
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: TEAL }} aria-hidden="true" />
              <p className="text-text-muted">
                Fill in your details below to generate a personal certificate marking your Shahadah —
                the declaration that there is no deity but Allah, and Muhammad&nbsp;ﷺ is His Messenger.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="shahadah-name" className="block text-sm font-medium text-text-main mb-2">
                  Your name (as it will appear on the certificate)
                </label>
                <input
                  id="shahadah-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Aaliyah Noor"
                  className="w-full px-4 py-3 rounded-arch text-sm focus:ring-2 focus:ring-primary transition-all"
                  style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                  data-testid="shahadah-name-input"
                />
              </div>

              <div>
                <label htmlFor="shahadah-date" className="block text-sm font-medium text-text-main mb-2">
                  Date of your Shahadah
                </label>
                <input
                  id="shahadah-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="w-full px-4 py-3 rounded-arch text-sm focus:ring-2 focus:ring-primary transition-all"
                  style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                  data-testid="shahadah-date-input"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none" htmlFor="shahadah-confirm">
                <input
                  id="shahadah-confirm"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded accent-primary shrink-0"
                  data-testid="shahadah-confirm-checkbox"
                />
                <span className="text-sm text-text-muted leading-relaxed">
                  I confirm that I have said the Shahadah and I am claiming this badge with sincerity
                </span>
              </label>

              <button
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-arch font-medium text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(90deg, ${TEAL}, ${LAVENDER})`, color: '#fff' }}
                data-testid="generate-certificate-btn"
              >
                {generating
                  ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  : <Sparkles className="w-5 h-5" aria-hidden="true" />}
                Generate my certificate
              </button>
            </div>
          </motion.div>
        )}

        {/* ══════════════ STEP 2 — Certificate preview ══════════════ */}
        {step === 2 && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
          >
            {/*
              Certificate canvas — 11 × 8.5 in landscape.
              Background PNG fills the container.
              Text overlay is pushed into the live area:
                - paddingTop 14%  → clears the top decorative border (~1 in)
                - paddingBottom 5% → uses the extra inch below "SIRATI" text
              All font sizes are ~30% larger than v1.
            */}
            <div
              ref={certRef}
              data-testid="shahadah-certificate"
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '11 / 8.5',
                overflow: 'hidden',
                borderRadius: '4px',
                boxShadow: '0 16px 48px rgba(74,173,168,0.18)',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              {/* ── Background image ── */}
              <img
                src="/images/shahadah-certificate-bg.png"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />

              {/* ── Text overlay ── */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  paddingTop: '10%',    // moved up — more room for larger text
                  paddingBottom: '6%',  // anchors Sirati line above the bottom border
                  paddingLeft: '8%',
                  paddingRight: '8%',
                  gap: 0,
                }}
              >
                {/* Crescent moon mark */}
                <Moon
                  aria-hidden="true"
                  style={{
                    color: TEAL,
                    width: '5.6%',
                    height: '5.6%',
                    marginBottom: '1.4%',
                    flexShrink: 0,
                  }}
                  strokeWidth={1.5}
                />

                {/* "CERTIFICATE OF SHAHADAH" */}
                <p style={{
                  color: LAVENDER,
                  fontSize: 'clamp(10px, 2.0%, 20px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  marginBottom: '1.6%',
                  lineHeight: 1,
                }}>
                  Certificate of Shahadah
                </p>

                {/* "This certifies that" */}
                <p style={{
                  color: '#6b7280',
                  fontSize: 'clamp(10px, 1.9%, 19px)',
                  marginBottom: '0.6%',
                  lineHeight: 1,
                }}>
                  This certifies that
                </p>

                {/* Member name — Great Vibes, allow wrapping so long names are never clipped */}
                <h2
                  data-testid="certificate-name"
                  style={{
                    fontFamily: "'Great Vibes', cursive",
                    color: TEAL,
                    fontSize: 'clamp(32px, 8.4%, 78px)',
                    lineHeight: 1.15,
                    marginBottom: '0.5%',
                    maxWidth: '84%',
                    wordBreak: 'break-word',
                  }}
                >
                  {name || 'Your Name'}
                </h2>

                {/* "has declared…" — own line, no date embedded */}
                <p style={{
                  color: '#6b7280',
                  fontSize: 'clamp(10px, 1.9%, 19px)',
                  marginBottom: '0.4%',
                  lineHeight: 1.4,
                }}>
                  has declared, with a sincere heart, the testimony of faith on
                </p>

                {/* Date — Great Vibes script, own line, smaller than name */}
                {date && (
                  <p
                    data-testid="certificate-date"
                    style={{
                      fontFamily: "'Great Vibes', cursive",
                      color: TEAL,
                      fontSize: 'clamp(20px, 4.7%, 47px)',
                      lineHeight: 1.2,
                      marginBottom: '2%',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatCertDate(date)}
                  </p>
                )}

                {/* Thin rule */}
                <div style={{
                  width: '28%',
                  height: '1px',
                  background: `linear-gradient(90deg, transparent, ${LAVENDER}60, transparent)`,
                  marginBottom: '2%',
                  flexShrink: 0,
                }} aria-hidden="true" />

                {/* Arabic */}
                <p
                  dir="rtl"
                  lang="ar"
                  style={{
                    color: '#1f2937',
                    fontSize: 'clamp(16px, 3.5%, 35px)',
                    lineHeight: 1.6,
                    marginBottom: '0.8%',
                    fontFamily: "'Scheherazade New', 'Amiri', serif",
                  }}
                >
                  فَاعْلَمْ أَنَّهُ لَا إِلَٰهَ إِلَّا اللَّهُ
                </p>

                {/* Transliteration */}
                <p style={{
                  color: '#9ca3af',
                  fontSize: 'clamp(9px, 1.6%, 16px)',
                  fontStyle: 'italic',
                  marginBottom: '0.5%',
                  lineHeight: 1,
                }}>
                  Fa'lam annahu lā ilāha illā Allāh
                </p>

                {/* Translation */}
                <p style={{
                  color: '#374151',
                  fontSize: 'clamp(10px, 1.8%, 18px)',
                  marginBottom: '0.4%',
                  lineHeight: 1,
                }}>
                  "So know that there is no deity except Allah."
                </p>

                {/* Surah citation */}
                <p style={{
                  color: '#9ca3af',
                  fontSize: 'clamp(8px, 1.5%, 15px)',
                  letterSpacing: '0.05em',
                  marginBottom: '2%',
                  lineHeight: 1,
                }}>
                  Surah Muhammad, 47:19 · Sahih International
                </p>

                {/* Moon divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2%',
                  marginBottom: '1.4%',
                  flexShrink: 0,
                }} aria-hidden="true">
                  <span style={{ width: '4%', height: '1px', background: `${LAVENDER}55` }} />
                  <Moon style={{ color: LAVENDER, width: '2.5%', height: '2.5%', flexShrink: 0 }} strokeWidth={1.6} />
                  <span style={{ width: '4%', height: '1px', background: `${LAVENDER}55` }} />
                </div>

                {/* "SIRATI · sirati.co" — +30%: 1.0% → 1.3% */}
                <p style={{
                  color: LAVENDER,
                  fontSize: 'clamp(7px, 1.3%, 13px)',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}>
                  Sirati · sirati.co
                </p>
              </div>
            </div>

            {/* ── Actions ── */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="min-h-[48px] px-6 flex items-center justify-center gap-2 rounded-arch font-medium text-sm transition-all disabled:opacity-50"
                style={{ background: `linear-gradient(90deg, ${TEAL}, ${LAVENDER})`, color: '#fff' }}
                data-testid="download-certificate-btn"
              >
                {downloading
                  ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  : <Download className="w-5 h-5" aria-hidden="true" />}
                Download certificate
              </button>
              <button
                onClick={() => setStep(1)}
                className="min-h-[48px] px-6 flex items-center justify-center rounded-arch font-medium text-sm transition-all hover:bg-primary/5"
                style={{ border: '1px solid var(--border)', color: 'var(--text-main)' }}
                data-testid="edit-certificate-details-btn"
              >
                Edit details
              </button>
            </div>

            <p className="text-center text-xs text-text-muted mt-4 italic">
              The "Shahadah Reflection" badge has been added to your collection. May Allah accept it from you.
            </p>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default ShahadahCertificatePage;
